import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Send, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

export default function ReplyDrawer({ inquiry, onClose, onSent }) {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [source, setSource] = useState("");

  const loadDraft = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/admin/inquiries/reply-draft", { inquiry_id: inquiry.id });
      setSubject(data.draft.subject);
      setBody(data.draft.body);
      setSource(data.source);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (inquiry) loadDraft(); }, [inquiry?.id]);

  const send = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await api.post("/admin/inquiries/reply", { inquiry_id: inquiry.id, subject, body });
      toast.success(`Reply sent to ${inquiry.email}`);
      onSent(inquiry.id, data.reply);
      onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <AnimatePresence>
      {inquiry && (
        <motion.aside initial={{ x: 480 }} animate={{ x: 0 }} exit={{ x: 480 }}
          transition={{ type: "spring", stiffness: 260, damping: 30 }}
          className="fixed top-0 right-0 bottom-0 z-[80] w-full sm:w-[460px] glass border-l border-[#E8DCCB] flex flex-col shadow-[-12px_0_40px_rgba(58,46,37,0.08)]"
          data-testid="reply-drawer">
          <div className="flex items-center justify-between px-5 h-16 border-b border-[#E8DCCB] bg-[#FFF3F8] shrink-0">
            <div>
              <p className="f-display text-lg leading-none">Reply to {inquiry.name}</p>
              <p className="text-[10px] tracking-[0.14em] uppercase text-[#8C7562] mt-1.5 truncate max-w-[300px]" data-testid="reply-to-email">{inquiry.email}</p>
            </div>
            <button onClick={onClose} className="text-[#8C7562] hover:text-[#3A2E25] transition-colors" data-testid="reply-close-btn"><X size={18} /></button>
          </div>
          <form onSubmit={send} className="flex-1 flex flex-col gap-4 p-5 overflow-y-auto">
            <div className="flex items-center justify-between">
              <span className="eyebrow">{loading ? "Drafting…" : source === "ai" ? "Draft written for you" : "Draft ready"}</span>
              <button type="button" onClick={loadDraft} disabled={loading} className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-[#A8785A] hover:text-[#3A2E25] disabled:opacity-50" data-testid="reply-redraft-btn">
                <Sparkles size={12} /> New draft
              </button>
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-[#8C7562] mb-2">Subject</label>
              <input className="field" value={subject} onChange={(e) => setSubject(e.target.value)} disabled={loading} data-testid="reply-subject-input" />
            </div>
            <div className="flex-1 flex flex-col">
              <label className="block text-xs font-bold uppercase tracking-wider text-[#8C7562] mb-2">Message</label>
              <textarea className="field flex-1 !min-h-[260px]" value={body} onChange={(e) => setBody(e.target.value)} disabled={loading} data-testid="reply-body-input" />
            </div>
            <button type="submit" disabled={sending || loading || !subject || !body} className="btn-primary justify-center" data-testid="reply-send-btn">
              <Send size={14} /> {sending ? "Sending…" : "Send reply"}
            </button>
          </form>
        </motion.aside>
      )}
    </AnimatePresence>
  );
}
